import { Component, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormErrorComponent } from 'shared/components/form-error/form-error.component';
import { PasswordInputComponent } from 'shared/components/password-input/password-input.component';
import { passwordPattern } from 'shared/validators/password-pattern-validator';
import { mustMatch } from 'shared/validators/must-match-validator';
import { BaseModal } from 'shared/abstract/base-modal';
import { ToastService, UserApiService } from 'shared/services';
import { ChangePassword } from 'shared/interfaces/change-password.interface';
import { catchError, EMPTY, Subscription } from 'rxjs';
import { unsubscribeFromAll } from 'shared/util/unsubscribeFromAll';

@Component({
    selector: 'app-change-password',
    templateUrl: './change-password.component.html',
    styleUrls: ['./change-password.component.scss'],
    imports: [ReactiveFormsModule, TranslateModule, FormErrorComponent, PasswordInputComponent],
})
export class ChangePasswordComponent extends BaseModal implements OnInit, OnDestroy {
    data: any;

    public changePasswordForm = new UntypedFormGroup({
        currentPassword: new UntypedFormControl('', [Validators.required]),
        newPassword: new UntypedFormControl('', [Validators.required, Validators.minLength(8), passwordPattern()]),
        repeatNewPassword: new UntypedFormControl('', [Validators.required, mustMatch('newPassword')]),
    });

    private subscriptions: { [key: string]: Subscription } = {};
    private userEmail!: string;

    constructor(
        private userApiService: UserApiService,
        private translateService: TranslateService,
        private toastService: ToastService,
        private destroyRef: DestroyRef,
    ) {
        super();
    }

    public ngOnInit(): void {
        this.userApiService
            .getLoginInformation()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((data) => (this.userEmail = data.email));
    }

    public cancel() {
        this.closeModalInstance(undefined);
    }

    public changePassword() {
        this.changePasswordForm.markAllAsTouched();
        ['currentPassword', 'newPassword', 'repeatNewPassword'].forEach((controlName) =>
            this.changePasswordForm.get(controlName)?.updateValueAndValidity(),
        );

        if (this.changePasswordForm.valid) {
            const body = {
                email: this.userEmail,
                password: this.changePasswordForm.value.newPassword,
                currentPassword: this.changePasswordForm.value.currentPassword,
            } as ChangePassword;

            this.subscriptions.changePassword = this.userApiService
                .changePassword(body)
                .pipe(
                    catchError((error) => {
                        if (error.status === 400) {
                            this.toastService.showToast({
                                body: 'changePassword.invalidCurrentPassword',
                                severity: 'error',
                                ignoreRemoval: true,
                            });
                            return EMPTY;
                        }
                        throw error;
                    }),
                )
                .subscribe(() => {
                    this.toastService.showToast({
                        body: 'changePassword.changeSuccess',
                        severity: 'success',
                        ignoreRemoval: true,
                    });
                    this.closeModalInstance(undefined);
                });
        }
    }

    public ngOnDestroy() {
        unsubscribeFromAll(this.subscriptions);
    }
}
